import { PARTICIPANT_HUB_LIGHT_TOKENS } from '@hub/common';
import {
  alpha,
  createTheme,
  type Theme,
  type ThemeOptions,
} from '@mui/material/styles';
import type { BrandTokens } from '@hub/common';
import type { PaletteMode } from '@mui/material';

const primaryDarkMain = '#9FA8DA';
const backgroundDarkDefault = '#12162A';

/** The diagonal-lines pattern drawn over the dark page colour. */
const darkStripes = `repeating-linear-gradient(45deg, ${alpha(primaryDarkMain, 0.17)} 0 1px, transparent 1px 13px)`;

/** Page background painted on `body` — every route has it; opaque surfaces
 *  (`Surface`, `ui.primarySurface`) sit on top. */
const bodyBackgroundCss = (
  backgroundColor: string,
  backgroundImage: string
): string => `
        body {
          background-color: ${backgroundColor};
          background-image: ${backgroundImage};
        }
  `;

/** `color-scheme` per mode: native scrollbars and form controls follow the
 *  palette. */
const htmlColorSchemeCss = (mode: PaletteMode): string => `
        html {
          color-scheme: ${mode};
        }
  `;

/** The page background as `sx`, for elements that must be an opaque cover
 *  over the app and still look like the page (the loading screen). */
export const pageBackgroundSx = (theme: Theme) => ({
  backgroundColor: theme.palette.background.default,
  backgroundImage: theme.ui.pageBackgroundImage,
});

type UiTokens = {
  surfaceBorder: string;
  cardRadius: number;
  shadow: string;
  shadowLift: string;
  /** Opaque primary-tinted surface (design --d-primary-bg) — surfaces that must not let the page background bleed through. */
  primarySurface: string;
  timelineDone: string;
  /** `background-image` of the page: the stripes, or `none` for a brand background. */
  pageBackgroundImage: string;
};

const lightUiTokens = (tokens: BrandTokens): UiTokens => ({
  surfaceBorder: 'rgba(0,0,0,0.12)',
  cardRadius: tokens.cardRadius,
  shadow: '0 1px 2px rgba(0,0,0,0.05), 0 1px 3px rgba(0,0,0,0.08)',
  shadowLift: tokens.shadowLift,
  primarySurface: tokens.primarySurface,
  timelineDone: '#689F38',
  pageBackgroundImage: tokens.backgroundImage,
});

const darkUiTokens: UiTokens = {
  surfaceBorder: 'rgba(255,255,255,0.13)',
  cardRadius: 12,
  shadow: '0 1px 2px rgba(0,0,0,0.40), 0 2px 6px rgba(0,0,0,0.30)',
  shadowLift: '0 8px 24px rgba(0,0,0,0.50), 0 2px 8px rgba(0,0,0,0.35)',
  primarySurface: '#282E48',
  timelineDone: '#9CCC65',
  pageBackgroundImage: darkStripes,
};

/** The MUI theme of `mode`. Light mode maps `tokens` (the Participant Hub
 *  tokens without a brand); dark mode ignores them. */
export const getThemeOptions = (
  mode: PaletteMode,
  tokens?: BrandTokens
): ThemeOptions => {
  const light = tokens ?? PARTICIPANT_HUB_LIGHT_TOKENS;
  const isLight = mode === 'light';
  const ui = isLight ? lightUiTokens(light) : darkUiTokens;
  const backgroundDefault = isLight ? light.background : backgroundDarkDefault;

  return {
    breakpoints: {
      values: {
        xs: 0,
        sm: 320,
        md: 768,
        lg: 992,
        xl: 1200,
      },
    },
    // https://github.com/mui/mui-x/issues/20238
    palette: {
      mode,
      ...(isLight
        ? {
            primary: {
              main: light.primary,
              hover: alpha(light.primary, 0.04),
              selected: alpha(light.primary, 0.08),
              focus: alpha(light.primary, 0.12),
              focusVisible: alpha(light.primary, 0.3),
              contrastText: light.onPrimary,
            },
            ...(tokens &&
              tokens.secondary !== PARTICIPANT_HUB_LIGHT_TOKENS.secondary && {
                secondary: {
                  main: tokens.secondary,
                  contrastText: tokens.onSecondary,
                },
              }),
            // Meets AA for 13px text on the row hover fill, not only on paper.
            warning: {
              main: '#ad4e00',
            },
            text: {
              primary: light.text,
              secondary: light.textSecondary,
              disabled: light.textDisabled,
              tertiary: light.textTertiary,
            },
            divider: alpha('#000000', 0.1),
            background: {
              default: light.background,
              paper: light.surface,
            },
          }
        : {
            primary: {
              main: primaryDarkMain,
              hover: alpha(primaryDarkMain, 0.08),
              selected: alpha(primaryDarkMain, 0.16),
              focus: alpha(primaryDarkMain, 0.16),
              focusVisible: alpha(primaryDarkMain, 0.3),
            },
            // Meets AA on the dark paper background.
            warning: {
              main: '#ffa726',
            },
            text: { tertiary: alpha('#FFFFFF', 0.54) },
            divider: alpha('#FFFFFF', 0.1),
            background: {
              default: backgroundDarkDefault,
              paper: '#171A2E',
            },
          }),
    },
    typography: {
      fontFamily: 'Lato, sans-serif',
      button: {
        textTransform: 'none',
      },
    },
    shape: {
      borderRadius: isLight ? light.radius : 8,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: `
        @font-face {
          font-family: Lato;
          src: url(/fonts/Lato-Regular.woff2) format('woff2');
          font-weight: normal;
          font-style: normal;
        }

        @font-face {
          font-family: Lato;
          src: url(/fonts/Lato-Semibold.woff2) format('woff2');
          font-weight: 600;
          font-style: normal;
        }

        @font-face {
          font-family: Lato;
          src: url(/fonts/Lato-Bold.woff2) format('woff2');
          font-weight: bold;
          font-style: normal;
        }

        @font-face {
          font-family: Lato;
          src: url(/fonts/Lato-Italic.woff2) format('woff2');
          font-style: italic;
        }

        @font-face {
          font-family: Kalam;
          src: url(/fonts/Kalam-Regular.woff2) format('woff2');
          font-weight: 400;
          font-style: normal;
          font-display: swap;
        }
        ${htmlColorSchemeCss(mode)}
        ${bodyBackgroundCss(backgroundDefault, ui.pageBackgroundImage)}
      `,
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '10rem',
          },
        },
        defaultProps: {
          disableElevation: true,
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            whiteSpace: 'pre-line',
          },
        },
      },
      MuiInputBase: {
        defaultProps: {
          disableInjectingGlobalStyles: true,
        },
      },
      // TODO: Update and use button designs
      // MuiButton: MuiButtonTheme as Components<
      //   Omit<Theme, 'components'>
      // >['MuiButton'],
    },
    ui,
  };
};

declare module '@mui/material/styles' {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- declaration merging
  interface Theme {
    ui: UiTokens;
  }
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- declaration merging
  interface ThemeOptions {
    ui: UiTokens;
  }
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- declaration merging
  interface SimplePaletteColorOptions {
    selected?: string;
    hover?: string;
    focus?: string;
    // meant to be used when item is hovered/focused and selected
    focusVisible?: string;
  }
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- declaration merging
  interface PaletteColor {
    selected?: string;
    hover?: string;
    focus?: string;
    focusVisible?: string;
  }
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- declaration merging
  interface TypeText {
    tertiary: string;
  }
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- declaration merging
  interface TypeTextOptions {
    tertiary?: string;
  }
}

/** The light participant theme of brand tokens. */
export const createBrandTheme = (tokens: BrandTokens): Theme =>
  createTheme(getThemeOptions('light', tokens));
